import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { PropertyCard } from "@/components/PropertyCard";
import { Testimonials } from "@/components/Testimonials";
import { BlogCard } from "@/components/BlogCard";
import { RadarPerspective } from "@/components/charts/RadarPerspective";
import { useApp } from "@/context/AppContext";
import { IMAGES } from "@/data/seed";
import { VERTICALS, DIFFERENCE_DIMENSIONS, JOURNEY_STEPS } from "@/lib/perspective";

const VerticalPreview = ({ vkey, image, blurb }) => {
  const v = VERTICALS[vkey];
  return (
    <Link to={`/${vkey}`} data-testid={`home-vertical-${vkey}`} className="group relative block overflow-hidden rounded-2xl">
      <div className="img-zoom relative h-[440px]">
        <img src={image} alt={v.fullName} loading="lazy" className="h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,15,12,0.25) 0%, rgba(5,15,12,0.6) 55%, rgba(5,15,12,0.93) 100%)" }} />
      </div>
      <div className="hero-copy absolute inset-0 flex flex-col justify-end p-7 text-white">
        <div className="overline !text-[#9ED3B8]">{v.fullName}</div>
        <h3 className="mt-2 font-serif text-3xl font-light">{blurb}</h3>
        <p className="mt-2 max-w-xs text-sm text-white/70">{v.tagline}</p>
        <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#9ED3B8] transition-transform duration-300 group-hover:translate-x-1">
          {v.cta} <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
};

export default function Home() {
  const { data } = useApp();
  const featured = data.properties.filter((p) => p.published && p.featured).slice(0, 3);
  const sampleRadar = VERTICALS.living.criteria.map((c, i) => ({ criterion: c.label, value: [84, 72, 80, 78, 82, 76, 85, 80][i] }));

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Bengaluru architecture" className="h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(5,15,12,0.94) 0%, rgba(5,15,12,0.78) 50%, rgba(5,15,12,0.55) 100%), linear-gradient(0deg, rgba(5,15,12,0.75) 0%, rgba(5,15,12,0) 40%)" }} />
        </div>
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="hero-copy container-arch relative flex min-h-screen flex-col justify-center pt-24 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <div className="mb-6 flex items-center gap-3">
              {["Living", "Invests", "Luxe"].map((w, i) => (
                <motion.span key={w} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.15 }} className="overline !text-[#9ED3B8]">
                  {w}{i < 2 && <span className="ml-3 text-white/30">/</span>}
                </motion.span>
              ))}
            </div>
            <h1 className="font-serif text-4xl font-light leading-[1.08] text-white sm:text-5xl lg:text-6xl">
              The Right Property Starts With the Right <span className="gold-gradient-text italic">Perspective.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/75">
              We help you discover real estate aligned with how you want to live, invest and grow.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/request" data-testid="hero-cta-requirement" className="btn-gold !px-7 !py-4 text-sm shadow-[0_10px_40px_var(--accent-a50)]">Find My Property <ArrowUpRight className="h-4 w-4" /></Link>
              <Link to="/properties" data-testid="hero-cta-properties" className="btn-outline-gold !border-white/40 !text-white hover:!bg-white hover:!text-[#071711]">Explore Properties</Link>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-16 max-w-2xl">
            <p className="font-serif text-xl font-light italic text-white/60">Fewer Options. Better Perspective. Higher Conviction.</p>
          </motion.div>
        </div>
      </section>

      {/* WHAT ARE YOU BUYING FOR */}
      <section className="container-arch py-20 lg:py-28">
        <Reveal>
          <div className="overline">Understand → Evaluate → Curate</div>
          <h2 className="mt-3 max-w-2xl font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">What are you buying for?</h2>
          <p className="mt-4 max-w-xl text-[var(--text-muted)]">Every property isn't meant for every buyer. We begin with why you're buying.</p>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          <VerticalPreview vkey="living" image={IMAGES.tileLiving} blurb="A home selected around your life." />
          <VerticalPreview vkey="invests" image={IMAGES.tileInvests} blurb="Real estate evaluated as an asset." />
          <VerticalPreview vkey="luxe" image={IMAGES.tileLuxe} blurb="Exceptional properties for exceptional expectations." />
        </div>
      </section>

      {/* THE ARCHEDIA DIFFERENCE */}
      <section className="border-y border-[var(--border-subtle)] bg-[var(--bg-card)] py-20 lg:py-28">
        <div className="container-arch grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="overline">The Archedia Difference</div>
            <h2 className="mt-3 font-serif text-3xl font-light leading-tight text-[var(--text-primary)] sm:text-4xl lg:text-5xl">
              A Property Can Be Excellent — And Still Not Be Right for You.
            </h2>
            <p className="mt-6 font-serif text-2xl font-light text-[var(--accent-gold)]">We don't begin with a project. We begin with you.</p>
          </Reveal>
          <Stagger className="flex flex-wrap gap-2.5">
            {DIFFERENCE_DIMENSIONS.map((d) => (
              <StaggerItem key={d}>
                <span className="inline-block rounded-full border border-[var(--border-subtle)] px-4 py-2 text-sm text-[var(--text-primary)] transition hover:border-[var(--accent-gold)] hover:text-[var(--accent-gold)]">{d}</span>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* PERSPECTIVE ENGINE TEASER */}
      <section className="container-arch py-20 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="overline">The Archedia Perspective</div>
            <h2 className="mt-3 font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">Consulting intelligence, translated into visual design.</h2>
            <p className="mt-5 text-[var(--text-muted)]">
              We don't just describe how we evaluate. We visualise it — radar charts, score rings, weighted criteria and micro-market intelligence, so you understand how Archedia thinks before you ever speak with an advisor.
            </p>
            <p className="mt-4 text-xs text-[var(--text-muted)]">Illustrative Archedia evaluation data, not objective market guarantees.</p>
            <Link to="/living" className="btn-outline-gold mt-7">See the Perspective Engine <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="arch-card p-6">
              <RadarPerspective data={sampleRadar} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW ARCHEDIA WORKS */}
      <section className="border-y border-[var(--border-subtle)] bg-[var(--bg-card)] py-20 lg:py-28">
        <div className="container-arch">
          <Reveal><div className="overline">How Archedia Works</div>
            <h2 className="mt-3 font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">A guided journey, not a listing feed.</h2>
          </Reveal>
          <div className="relative mt-14">
            <div className="absolute left-0 top-6 hidden h-px w-full bg-[var(--border-subtle)] lg:block" />
            <motion.div className="absolute left-0 top-6 hidden h-px origin-left bg-[var(--accent-gold)] lg:block" style={{ width: "100%" }} initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.8, ease: "easeInOut" }} />
            <div className="grid gap-8 lg:grid-cols-5">
              {JOURNEY_STEPS.map((s, i) => (
                <motion.div key={s.no} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.6 }} className="relative">
                  <div className="relative z-10 mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-[var(--accent-gold)] bg-[var(--bg-card)] font-mono text-sm text-[var(--accent-gold)]">{s.no}</div>
                  <h4 className="font-serif text-xl text-[var(--text-primary)]">{s.title}</h4>
                  <p className="mt-2 text-sm text-[var(--text-muted)]">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      {featured.length > 0 && (
        <section className="container-arch py-20 lg:py-28">
          <Reveal className="flex items-end justify-between">
            <div>
              <div className="overline">Curated</div>
              <h2 className="mt-3 font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">Featured perspectives</h2>
            </div>
            <Link to="/properties" className="hidden items-center gap-2 text-sm font-medium text-[var(--accent-gold)] sm:inline-flex">View all <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featured.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
        </section>
      )}

      <Testimonials />

      {data.blogs?.length > 0 && (
        <section className="container-arch py-20 lg:py-28" data-testid="home-blog-preview">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">Latest from the Archedia desk.</h2>
              </div>
              <Link to="/blog" data-testid="home-blog-viewall" className="btn-outline-gold !py-2.5 text-xs">All articles <ArrowUpRight className="h-4 w-4" /></Link>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {data.blogs.slice(0, 3).map((b) => <BlogCard key={b.id} post={b} compact />)}
          </div>
        </section>
      )}

      {/* CLOSING CTA */}
      <section className="relative overflow-hidden py-24" style={{ background: "#071711" }}>
        <div className="grid-bg absolute inset-0 opacity-30" />
        <div className="container-arch relative text-center">
          <Reveal>
            <p className="mx-auto max-w-2xl font-serif text-2xl font-light leading-snug text-white sm:text-3xl">
              Different goals require different properties. Different properties require the right perspective.
            </p>
            <div className="mx-auto my-8 h-px w-24 bg-[var(--accent-gold)]" />
            <h2 className="gold-gradient-text font-serif text-4xl font-light sm:text-5xl">Perspective Builds Possibilities.</h2>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/request" className="btn-gold !bg-[var(--accent-gold)] !text-[#071711]">Find My Property <ArrowUpRight className="h-4 w-4" /></Link>
              <Link to="/contact" className="btn-outline-gold">Talk to Archedia</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
